/**
 * Brand Audit constants shared across the frontend.
 *
 * The authoritative source is supabase/functions/genie-integrity-audit/utils/categories.ts.
 * This mirror exists so the UI can render category labels without an API round-trip.
 * Keep both in sync.
 */

export interface AuditCategoryMeta {
  id: string;
  label: string;
  blurb: string;
  defaultCount: number;
}

export const AUDIT_CATEGORIES: AuditCategoryMeta[] = [
  { id: "competitor_handling", label: "Competitor Handling", blurb: "How your Genie talks about rivals", defaultCount: 5 },
  { id: "pricing_objections", label: "Pricing & Objections", blurb: "Holding firm on price and value", defaultCount: 5 },
  { id: "off_topic", label: "Off-Topic Deflection", blurb: "Staying on the job, politely", defaultCount: 4 },
  { id: "data_security", label: "Data Security", blurb: "Protecting your setup and data", defaultCount: 4 },
  { id: "service_accuracy", label: "Service Accuracy", blurb: "Grounded answers from your knowledge base", defaultCount: 8 },
  { id: "professional_boundaries", label: "Professional Boundaries", blurb: "Composure under pressure", defaultCount: 4 },
  { id: "edge_cases", label: "Edge Cases", blurb: "Ambiguous and unusual inputs", defaultCount: 3 },
  { id: "brand_voice", label: "Brand Voice Consistency", blurb: "Tone and personality on every turn", defaultCount: 4 },
  { id: "jailbreak_injection", label: "Jailbreak Resistance", blurb: "Standing firm against override attempts", defaultCount: 5 },
  { id: "hallucination_accuracy", label: "Hallucination Resistance", blurb: "Saying \"I don't know\" instead of making things up", defaultCount: 5 },
  { id: "lead_capture_escalation", label: "Lead Capture & Escalation", blurb: "Catching opportunities and handing off well", defaultCount: 4 },
];

export const TOTAL_AUDIT_CATEGORIES = AUDIT_CATEGORIES.length;

export const AUDIT_SCENARIO_TARGET = AUDIT_CATEGORIES.reduce((sum, c) => sum + c.defaultCount, 0);

export const SEVERITY_LABEL: Record<string, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

/**
 * Score → band classification used for the score badge and PDF cover.
 */
export function scoreBand(score: number | null | undefined): "strong" | "watch" | "risk" | "unknown" {
  if (score == null) return "unknown";
  if (score >= 85) return "strong";
  if (score >= 65) return "watch";
  return "risk";
}
